import { JSX, useEffect, useRef, useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import { flushSync } from 'react-dom';
import { ButtonDivIcon } from '../button-div-icon';
import { TranslationService } from '../../../../../services/translation.service';
import type { IStation } from '../../../../../models/labs/micromobility/station.model';
import darkBikeIcon from '../../../../../../assets/icons/micromobility-bike-dark.svg';
import darkScooterMarkerBlueIcon from '../../../../../../assets/icons/micromobility-bike-dark-marker-blue-outline.svg';
import DataUrlHelper from '../../../../../helpers/data-url.helper';
import DeviceHelper, { DeviceType } from '../../../../../helpers/device.helper';
import { AnalyticsService } from '../../../../../services/analytics-service';

function createAvailableVehiclesText(station: IStation): string {
  if (station.availableVehicles === 1) {
    return `${station.availableVehicles} ${TranslationService.get(`travelplanner.micromobility.formfactor.${station.formFactor.toLowerCase()}.singular`).toLowerCase()} ${TranslationService.get(
      'travelplanner.micromobility.available.singular'
    )}`;
  }
  return `${station.availableVehicles} ${TranslationService.get(`travelplanner.micromobility.formfactor.${station.formFactor.toLowerCase()}.plural`).toLowerCase()} ${TranslationService.get(
    'travelplanner.micromobility.available.plural'
  )}`;
}

function createScreenReaderText(station: IStation): string {
  return `${TranslationService.get('travelplanner.micromobility.station')} ${station.name} ${createAvailableVehiclesText(station)}`;
}

// === MARKERS ===
function createMarkerHtml(station: IStation, isClicked: boolean, isHighlighted: boolean): string {
  const icon = isClicked || isHighlighted ? darkScooterMarkerBlueIcon : darkBikeIcon;
  const className = isClicked || isHighlighted ? 'micromobility-marker--clicked__icon w-100 h-100' : 'micromobility-marker__icon me-1';
  return (
    `<img src="${DataUrlHelper.encode(icon)}" alt="" class="${className}" />` +
    (isClicked || isHighlighted ? '' : `<span class="visually-hidden">${createScreenReaderText(station)}</span>`) +
    `<span class="${isClicked || isHighlighted ? 'd-none' : ''}" aria-hidden="true">${station.availableVehicles}</span>`
  );
}

function createDefaultIcon(station: IStation, isHighlighted: boolean): L.DivIcon {
  return new ButtonDivIcon({
    className: 'button-div-icon station-marker micromobility-marker d-flex flex-row align-items-center justify-content-center bg-white border border-blue-1000 text-color-blue-1000',
    iconSize: [48, 24],
    iconAnchor: [24, 12],
    popupAnchor: [0, -10],
    html: createMarkerHtml(station, false, isHighlighted),
  });
}

function createClickedIcon(station: IStation, isHighlighted: boolean): L.DivIcon {
  return new ButtonDivIcon({
    className: 'button-div-icon micromobility-marker--clicked',
    iconSize: [30, 38],
    iconAnchor: [15, 25],
    popupAnchor: [0, -10],
    html: createMarkerHtml(station, true, isHighlighted),
  });
}

function StationMarker({ station, isHighlighted }: { station: IStation; isHighlighted: boolean }): JSX.Element {
  // === STATE ===
  const [icon, setIcon] = useState(createDefaultIcon(station, isHighlighted));
  const [zIndex, setZIndex] = useState(0);

  // === REFS ===
  const markerRef = useRef<L.Marker>(null!);

  useEffect(() => {
    if (isHighlighted) {
      setTimeout(() => {
        markerRef.current?.openPopup();
        setTimeout(() => {
          markerRef.current?.fire('popupopen', { popup: markerRef.current.getPopup() });
        }, 100);
      }, 100);
    }
  }, [isHighlighted]);

  // === HELPER FUNCTIONS ===
  function getOperatorTranslationKey(): string {
    if (station.operatorName.toLowerCase() === 'styr & ställ') {
      return 'styrochstall';
    }
    return '';
  }

  function createLinkHtml(linkStation: IStation): string | undefined {
    const deviceType = DeviceHelper.getDeviceType();
    if (![DeviceType.Android, DeviceType.iOS].includes(deviceType)) {
      return undefined;
    }
    const deviceSrc = deviceType === DeviceType.Android ? linkStation.rentalUriAndroid : linkStation.rentalUriIos;
    return `
      <a href="${deviceSrc}" rel="noopener noreferrer" class="micromobility-popup__link link--external">
        ${TranslationService.get('travelplanner.micromobility.bookwith')} ${linkStation.operatorName}
      </a>
    `;
  }

  // === VARIABLES ===
  const offerText = TranslationService.get(`travelplanner.micromobility.${getOperatorTranslationKey()}.offerinformationtext`);
  const linkHtml = createLinkHtml(station);
  const moreInfo = linkHtml ? undefined : TranslationService.get(`travelplanner.micromobility.${getOperatorTranslationKey()}.moreinfo`);
  const vehiclesAvailable = !!station.availableVehicles;

  // === EVENT HANDLERS ===
  function handleOnPopupOpen(popupEvent: L.PopupEvent) {
    AnalyticsService.addEvent(AnalyticsService.LabsMicromobility, 'Klick på hyrcykelstation', station.operatorName);

    // Force a synchronous update to ensure the popup is rendered before we try to focus it
    flushSync(() => {});

    // Inspired by http://melmo.github.io/accessibility/code/dist/leaflet.html
    // Focus on the opened popup
    const popups = popupEvent.popup.getElement()?.getElementsByClassName('micromobility-popup');
    if (!popups || popups.length === 0) {
      return;
    }
    for (let i = 0; i < popups.length; i += 1) {
      const popup = popups[i] as HTMLElement;
      popup.setAttribute('tabindex', '-1');
      popup.style.outline = 'none'; // Workaround for hiding built-in focus marker, see NV-4097
      popup.focus();
    }

    // Fix the a11y attributes on the close button
    const closeButtons = popupEvent.popup.getElement()?.getElementsByClassName('leaflet-popup-close-button');
    if (closeButtons && closeButtons.length > 0) {
      const closeButton = closeButtons[0] as HTMLLinkElement;
      closeButton.setAttribute('role', 'button');
      closeButton.setAttribute('aria-label', TranslationService.get('common.map.closepopup'));
      closeButton.href = '#';
    }

    const buttons = popupEvent.popup.getElement()?.getElementsByClassName('micromobility-popup__link');
    if (buttons && buttons.length > 0) {
      const button = buttons[0] as HTMLLinkElement;
      button.addEventListener('click', () => AnalyticsService.addEvent(AnalyticsService.LabsMicromobility, 'Klick på boka i popup', station.operatorName));
    }

    setIcon(createClickedIcon(station, false));
    setZIndex(1000);
  }

  function handleOnPopupClose() {
    setIcon(createDefaultIcon(station, false)); // Set the icon back to the default when the popup is closed
    setZIndex(0);
  }

  return (
    <Marker
      key={`${station.latitude}-${station.longitude}`}
      icon={icon}
      position={[station.latitude, station.longitude]}
      zIndexOffset={zIndex}
      ref={markerRef}
      eventHandlers={{
        popupopen: handleOnPopupOpen,
        popupclose: handleOnPopupClose,
      }}
    >
      <Popup autoPan={true} offset={[0, -10]}>
        <div className="micromobility-popup">
          <h3 className="micromobility-popup__name">
            {station.name} ({station.operatorName})
          </h3>
          <div>{createAvailableVehiclesText(station)}</div>
          {!vehiclesAvailable && <div className="mb-1 micromobility-search__warning-text">{TranslationService.get('travelplanner.micromobility.vehiclenolongeravailable')}</div>}
          {vehiclesAvailable && offerText && <div className="micromobility-popup__offer-text mt-2 px-1" dangerouslySetInnerHTML={{ __html: offerText }}></div>}
          {vehiclesAvailable && moreInfo && <div className="micromobility-popup__more-info mt-2" dangerouslySetInnerHTML={{ __html: moreInfo }} />}
          {vehiclesAvailable && linkHtml && <div className="micromobility-popup__more-info mt-2 btn--as-link w-100 text-start" dangerouslySetInnerHTML={{ __html: linkHtml }} />}
        </div>
      </Popup>
    </Marker>
  );
}

export default StationMarker;
